import React, { useState, useEffect, FC } from 'react'
import Form, { FormComponentProps } from 'antd/lib/form'
import { Input, Button, Select } from 'antd'
import { Reseller, ServiceProvider, UserInviteFormValues } from '../../types'
import { serviceProviderService } from '../../services'
import styles from './index.module.less'

interface UserInviteFormProps extends FormComponentProps {
	submit(values: UserInviteFormValues): Promise<void>
	type?: 'reseller' | 'service_provider'
}

const InviteForm: FC<UserInviteFormProps> = ({ type = 'service_provider', ...props }) => {
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([])
	const [resellers, setResellers] = useState<Reseller[]>(null)
	const orgTerm = type === 'service_provider' ? 'Service Provider' : 'Reseller'

	const onSubmit = (): void => {
		setIsLoading(true)
		props.form.validateFields(
			async (errors, values): Promise<void> => {
				if (errors) {
					setIsLoading(false)
					return
				}
				const entity_ids = typeof values.entity_ids === 'number' ? [values.entity_ids] : values.entity_ids
				await props.submit({ ...values, entity_type: type, entity_ids })
				setIsLoading(false)
			}
		)
	}

	const serviceProviderSelect = () =>
		props.form.getFieldDecorator('entity_ids', {
			rules: [
				{
					required: true,
					message: `${orgTerm} is required`
				}
			]
		})(
			<Select
				mode='multiple'
				showSearch
				filterOption={(input, option: React.ReactElement) => option.props.children.toLowerCase().includes(input.toLowerCase())}
				placeholder={`Select a ${orgTerm}`}
			>
				{serviceProviders.map(serviceProvider => (
					<Select.Option value={serviceProvider.id} key={serviceProvider.id}>
						{serviceProvider.name}
					</Select.Option>
				))}
			</Select>
		)

	const resellerSelect = () => {
		const handleServiceProviderChange = async (serviceProviderId: number) => {
			const { data } = await serviceProviderService.getChildren(serviceProviderId)
			setResellers(data)
		}

		return (
			<>
				<Select
					showSearch
					filterOption={(input, option: React.ReactElement) => option.props.children.toLowerCase().includes(input.toLowerCase())}
					onChange={handleServiceProviderChange}
					placeholder={`Select a Service Provider`}
				>
					{serviceProviders?.map(({ id, name }) => (
						<Select.Option value={id} key={id}>
							{name}
						</Select.Option>
					))}
				</Select>
				{props.form.getFieldDecorator('entity_ids', {
					rules: [
						{
							required: true,
							message: `Reseller is required`
						}
					]
				})(
					<Select
						showSearch
						disabled={resellers === null}
						filterOption={(input, option: React.ReactElement) => option.props.children.toLowerCase().includes(input.toLowerCase())}
						placeholder={`Select a Reseller`}
					>
						{resellers?.map(({ id, name }) => (
							<Select.Option value={id} key={id}>
								{name}
							</Select.Option>
						))}
					</Select>
				)}
			</>
		)
	}

	useEffect(() => {
		const getServiceProviders = async (): Promise<ServiceProvider[]> => {
			const { data } = await serviceProviderService.getAll()
			return data
		}

		getServiceProviders().then(setServiceProviders)
	}, [])

	return (
		<Form className={styles.form} hideRequiredMark layout='horizontal' labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} labelAlign='left'>
			<Form.Item label={orgTerm}>{type === 'service_provider' ? serviceProviderSelect() : resellerSelect()}</Form.Item>
			<Form.Item hasFeedback label='Email'>
				{props.form.getFieldDecorator('email', {
					rules: [
						{
							whitespace: true,
							required: true,
							message: 'Email is required'
						},
						{
							type: 'email',
							message: 'Please enter a valid email'
						}
					]
				})(<Input placeholder='Enter Email' spellCheck={false} type='text' />)}
			</Form.Item>
			<div className={styles.submitButton}>
				<Button type='primary' onClick={onSubmit}>
					{isLoading ? 'Saving' : 'Save'}
				</Button>
			</div>
		</Form>
	)
}

export default Form.create<UserInviteFormProps>()(InviteForm)
